import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, Loader2Icon, MinusIcon, PlusIcon, ScanIcon, XIcon } from 'lucide-react';
import { useZoomPan } from './useZoomPan';
import { preloadImage, preloadImages } from '../../utils/preload';
export interface LightboxItem {
  src: string;
  alt: string;
  caption: string;
}
interface LightboxProps {
  items: LightboxItem[];
  /** Index of the open item, or null when closed. */
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
  label: string;
  counterNoun?: string;
}

/** Window event other UI (the sticky purchase bar) listens to. */
export const LIGHTBOX_EVENT = 'wfp-lightbox-toggle';
const iconButton = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-out hover:bg-white/20 disabled:opacity-30 disabled:hover:bg-white/10 focus:outline-none focus-visible:ring-4 focus-visible:ring-olive-light/70';

/**
 * Full-screen viewer for original high-resolution pages.
 * Pinch / wheel / double-tap / buttons to zoom, drag to pan, swipe or arrow
 * keys to change image, Escape to close. Focus is trapped and restored.
 */
export function Lightbox({
  items,
  index,
  onClose,
  onNavigate,
  label,
  counterNoun = 'Image'
}: LightboxProps) {
  const open = index !== null;
  const current = index !== null ? items[index] : null;
  const count = items.length;
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const go = useCallback((direction: 1 | -1) => {
    if (index === null) return;
    const next = index + direction;
    if (next >= 0 && next < count) onNavigate(next);
  }, [index, count, onNavigate]);
  const zoom = useZoomPan(stageRef, imageRef, {
    onSwipe: go
  });
  const {
    reset
  } = zoom;

  // Keep the previous page on screen until the next one is decoded — no black flash.
  const [shown, setShown] = useState<LightboxItem | null>(null);
  const loading = Boolean(current && shown?.src !== current.src);
  useEffect(() => {
    if (!current || index === null) {
      setShown(null);
      return;
    }
    let cancelled = false;
    preloadImage(current.src).then(() => {
      if (!cancelled) setShown(current);
    });
    // Warm the neighbours so the next swipe is instant.
    const neighbours = [items[index - 1], items[index + 1]].filter(Boolean).map(item => item.src);
    preloadImages(neighbours);
    return () => {
      cancelled = true;
    };
  }, [current, index, items]);
  useEffect(() => {
    reset();
  }, [shown?.src, reset]);
  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new CustomEvent(LIGHTBOX_EVENT, {
      detail: true
    }));
    const frame = window.requestAnimationFrame(() => closeRef.current?.focus({
      preventScroll: true
    }));
    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      window.dispatchEvent(new CustomEvent(LIGHTBOX_EVENT, {
        detail: false
      }));
      previousFocus?.focus?.({
        preventScroll: true
      });
      // Return the visitor to exactly where they opened the viewer.
      if (Math.abs(window.scrollY - scrollY) > 1) window.scrollTo({
        top: scrollY,
        behavior: 'auto'
      });
    };
  }, [open]);
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      case 'ArrowRight':
        event.preventDefault();
        go(1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        go(-1);
        break;
      case '+':
      case '=':
        zoom.zoomIn();
        break;
      case '-':
        zoom.zoomOut();
        break;
      case 'Tab':
        {
          const all = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
          const nodes = Array.from(all ?? []).filter(node => node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden');
          if (nodes.length === 0) return;
          const first = nodes[0];
          const last = nodes[nodes.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
          break;
        }
      default:
        break;
    }
  };
  if (typeof document === 'undefined') return null;
  return createPortal(<AnimatePresence data-id="element-82" data-mp="23249bbn24jbm">
      {open && current && index !== null && <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`${label} — ${current.caption}`} onKeyDown={onKeyDown} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-0 z-50 flex flex-col bg-charcoal-deep/[0.97] text-white" style={{
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-83" data-mp="1jh42p6twa9qp">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-2 sm:px-6" data-id="element-84" data-mp="1xfkh7jy014je">
            <div className="min-w-0" aria-live="polite" data-id="element-85" data-mp="277gguzfqh2pq">
              <p className="label-tech text-olive-light" data-id="element-86" data-mp="277zysg5dvr3r">
                {counterNoun} {index + 1} of {count}
              </p>
              <p className="truncate text-[1rem] font-semibold leading-tight" data-id="element-87" data-mp="2g7wie3cx1zry">
                {current.caption}
              </p>
            </div>
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Close viewer" className={iconButton} data-id="element-88" data-mp="1dvru3dm0nn8y">
              <XIcon className="h-5 w-5" aria-hidden data-id="element-89" data-mp="1ittcrxfj5kjw" />
            </button>
          </div>

          <div ref={stageRef} {...zoom.handlers} className="relative min-h-0 flex-1 touch-none select-none overflow-hidden" style={{
        cursor: zoom.scale > 1 ? zoom.interacting ? 'grabbing' : 'grab' : 'zoom-in'
      }} data-id="element-90" data-mp="1mtlpmmbyicv2">
            <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6" data-id="element-91" data-mp="21j9nteoucvek">
              {shown && <img ref={imageRef} key={shown.src} src={shown.src} alt={shown.alt} draggable={false} className={`max-h-full max-w-full rounded-[2px] object-contain transition-opacity duration-150 ease-out ${loading ? 'opacity-60' : 'opacity-100'}`} style={{
            transform: `translate3d(${zoom.offset.x}px, ${zoom.offset.y}px, 0) scale(${zoom.scale})`,
            transition: zoom.interacting ? 'opacity 150ms ease-out' : 'transform 180ms cubic-bezier(0.23, 1, 0.32, 1), opacity 150ms ease-out'
          }} data-id="element-92" data-mp="26kgvldqzhul8" />}
            </div>
            {loading && <div className="pointer-events-none absolute inset-0 flex items-center justify-center" role="status" data-id="element-93" data-mp="rjp57yy8n8f8">
                <span className="flex items-center gap-2 rounded-full bg-charcoal/90 px-3.5 py-2 text-[0.85rem] font-semibold text-ivory ring-1 ring-white/15" data-id="element-94" data-mp="ild8rgaoxgev">
                  <Loader2Icon className="h-4 w-4 animate-spin text-olive-light" aria-hidden data-id="element-95" data-mp="dm0jyq09dz9n" />
                  Loading page…
                </span>
              </div>}

            <button type="button" onClick={() => go(-1)} disabled={index === 0} aria-label="Previous" onPointerDown={event => event.stopPropagation()} className={`${iconButton} absolute left-4 top-1/2 hidden -translate-y-1/2 md:flex`} data-id="element-96" data-mp="2ybpy10t51h1a">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-97" data-mp="sdjimsnho4q9" />
            </button>
            <button type="button" onClick={() => go(1)} disabled={index === count - 1} aria-label="Next" onPointerDown={event => event.stopPropagation()} className={`${iconButton} absolute right-4 top-1/2 hidden -translate-y-1/2 md:flex`} data-id="element-98" data-mp="xajob2gff5gu">
              <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-99" data-mp="1g8w0kx2mwvgn" />
            </button>
          </div>

          <div className="shrink-0 border-t border-white/10 px-3 pb-2 pt-2 sm:px-6" data-id="element-100" data-mp="23y85vluucjdo">
            <div className="flex items-center justify-between gap-2" data-id="element-101" data-mp="28pgg2k3zw8rw">
              <button type="button" onClick={() => go(-1)} disabled={index === 0} aria-label="Previous" className={`${iconButton} md:invisible`} data-id="element-102" data-mp="28qrmkrotrump">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-103" data-mp="63b45ydcvzmd" />
              </button>

              <div className="flex items-center gap-2" data-id="element-104" data-mp="21g40x75vsa6">
                <button type="button" onClick={zoom.zoomOut} disabled={!zoom.canZoomOut} aria-label="Zoom out" className={iconButton} data-id="element-105" data-mp="21zmcf0q4ioq">
                  <MinusIcon className="h-5 w-5" aria-hidden data-id="element-106" data-mp="3jhfw2sdmrtnr" />
                </button>
                <span className="w-12 text-center text-[14px] font-semibold tabular-nums text-parchment" data-id="element-107" data-mp="3eil0sqyh8cmr">
                  {Math.round(zoom.scale * 100)}%
                </span>
                <button type="button" onClick={zoom.zoomIn} disabled={!zoom.canZoomIn} aria-label="Zoom in" className={iconButton} data-id="element-108" data-mp="ytjpfz36v056">
                  <PlusIcon className="h-5 w-5" aria-hidden data-id="element-109" data-mp="8vzy9z9tq42k" />
                </button>
                <button type="button" onClick={zoom.reset} disabled={!zoom.canZoomOut} aria-label="Fit to screen" className={iconButton} data-id="element-110" data-mp="45jk2cy6ief5">
                  <ScanIcon className="h-5 w-5" aria-hidden data-id="element-111" data-mp="3rbakuwnbjvg4" />
                </button>
              </div>

              <button type="button" onClick={() => go(1)} disabled={index === count - 1} aria-label="Next" className={`${iconButton} md:invisible`} data-id="element-112" data-mp="3m9tjz14u48zj">
                <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-113" data-mp="1fkoobldykl0v" />
              </button>
            </div>
            <p className="mt-1.5 text-center text-[0.78rem] text-muted" data-id="element-114" data-mp="1bjmp34k1k8yw">
              Pinch, scroll or double-tap to zoom · drag to pan
            </p>
          </div>
        </motion.div>}
    </AnimatePresence>, document.body);
}